import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, CircularProgress, Alert, Grid, Chip, Stack, Divider, styled, Table, TableBody, TableCell, TableHead, TableRow, Button, Avatar, alpha } from '@mui/material';
import { AttachMoney, Business, CalendarMonth, Percent, TrendingUp, AutoAwesome, Stars, Psychology as PsychologyIcon, ArrowBack, LocationOn, Hotel as HotelIcon } from '@mui/icons-material'; // Example icons
import { api } from '@services';

interface Deal {
  id: string;
  name: string;
  hotel_id?: string;
  hotel_data: {
    name: string;
    address: string;
    city: string;
    state: string;
  };
  financial_metrics: {
    category: string;
    year: number;
    value: number;
    source?: 'revenue' | 'expense' | 'confirmed' | 'ocr';
  }[];
  insights: {
    investmentRisk: string;
    profitabilityScore: number;
    valuationEstimate: {
      low: number;
      high: number;
    };
    summary: string;
    keyMetrics?: {
      totalRevenue: number;
      totalExpenses: number;
      netOperatingIncome: number;
      profitMargin: number;
      occupancyRate?: number;
      adr?: number;
      revpar?: number;
      capRate?: number;
      pricePerKey?: number;
      revenueMultiple?: number;
    };
    valuationMethods?: {
      incomeApproach?: {
        value: number;
        capRate: number;
      };
      marketApproach?: {
        value: number;
        multiple: number;
      };
      replacementCost?: {
        value: number;
        costPerKey: number;
      };
    };
    dealAnalysis?: {
      askingPriceAnalysis: string;
      negotiationLeverage: string;
      recommendedOfferPrice: number;
    };
  };
  asking_price: number;
  created_at: string;
  hotel_analysis?: any;
  valuation_methods?: {
    incomeApproach?: {
      value: number;
      capRate: number;
    };
    marketApproach?: {
      value: number;
      multiple: number;
    };
    replacementCost?: {
      value: number;
      costPerKey: number;
    };
  };
  cap_rate?: number;
  price_per_key?: number;
  revenue_multiple?: number;
  recommended_offer?: number;
  negotiation_leverage?: string;
}

// Styled Components for improved UI
const SectionTitle = styled(Typography)(({ theme }) => ({
  variant: 'h5',
  fontWeight: 700,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3),
  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  paddingBottom: theme.spacing(1.5),
  fontSize: '1.5rem',
  letterSpacing: '-0.01em',
  lineHeight: 1.4,
}));

const MetricCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  },
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
}));

const KeyMetricValue = styled(Typography)(({ theme }) => ({
  variant: 'h6',
  fontWeight: 700,
  color: theme.palette.text.primary,
  fontSize: '1.5rem',
  lineHeight: 1.4,
  letterSpacing: '-0.01em',
  marginBottom: theme.spacing(0.5),
}));

const KeyMetricLabel = styled(Typography)(({ theme }) => ({
  variant: 'caption',
  color: theme.palette.text.secondary,
  display: 'block',
  marginBottom: theme.spacing(0.5),
  fontWeight: 500,
  fontSize: '0.875rem',
  letterSpacing: '0.01em',
  textTransform: 'uppercase',
}));

const FinancialMetricValue = styled(Typography)(({ theme }) => ({
  variant: 'body1',
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontSize: '1.125rem',
  letterSpacing: '-0.01em',
  lineHeight: 1.4,
}));

const FinancialMetricLabel = styled(Typography)(({ theme }) => ({
  variant: 'caption',
  color: theme.palette.text.secondary,
  display: 'block',
  marginBottom: theme.spacing(0.5),
  fontWeight: 500,
  fontSize: '0.875rem',
  letterSpacing: '0.01em',
}));

const MetricChange = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(1),
  '&.positive': {
    color: theme.palette.success.main,
  },
  '&.negative': {
    color: theme.palette.error.main,
  },
}));

const SourceChip = styled(Chip)<{ sourceType?: 'revenue' | 'expense' | 'confirmed' | 'ocr' }>(({ theme, sourceType }) => {
  const getChipColor = () => {
    switch (sourceType) {
      case 'confirmed':
        return { backgroundColor: theme.palette.success.light, color: theme.palette.success.main };
      case 'revenue':
        return { backgroundColor: theme.palette.primary.light, color: theme.palette.primary.main };
      case 'expense':
        return { backgroundColor: theme.palette.error.light, color: theme.palette.error.main };
      case 'ocr':
        return { backgroundColor: theme.palette.warning.light, color: theme.palette.warning.main };
      default:
        return {};
    }
  };
  const chipColor = getChipColor();
  return {
    ...chipColor,
    borderRadius: '4px',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    height: '20px',
    padding: '2px 6px',
  };
});

const DealDetails: React.FC = () => {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const [deal, setDeal] = useState<Deal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hotelAnalysis, setHotelAnalysis] = useState<any>(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState<boolean>(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDeal = async () => {
      try {
        const response = await api.get(`/deals/${dealId}`);
        setDeal(response.data);
      } catch (err: any) {
        console.error('Error fetching deal:', err);
        setError(err.response?.data?.error || 'Failed to fetch deal');
      } finally {
        setLoading(false);
      }
    };
    fetchDeal();
  }, [dealId]);

  // Fetch hotel AI analysis when component mounts and when deal.hotel_id changes
  useEffect(() => {
    const fetchHotelAnalysis = async () => {
      if (!deal?.hotel_id) return;

      setLoadingAnalysis(true);
      setAnalysisError(null);

      try {
        const response = await api.get(`/hotels/${deal.hotel_id}/ai-analysis`);
        if (response.data) {
          setHotelAnalysis(response.data);
        }
      } catch (error) {
        console.log('Could not fetch hotel AI analysis:', error);
        setAnalysisError('Unable to load hotel AI analysis');
      } finally {
        setLoadingAnalysis(false);
      }
    };

    fetchHotelAnalysis();
  }, [deal?.hotel_id]);

  const groupFinancialsByCategory = (metrics: Deal['financial_metrics']) => {
    return metrics.reduce((acc, metric) => {
      const group = metric.category.includes('Revenue') ? 'Revenue' : metric.category.includes('Expense') ? 'Expenses' : metric.category.includes('Income') ? 'Income' : 'Other';

      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(metric);
      return acc;
    }, {} as Record<string, typeof metrics>);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getInvestmentScoreColor = (score: number) => {
    if (score >= 80) return 'success.main';
    if (score >= 60) return 'primary.main';
    if (score >= 40) return 'warning.main';
    return 'error.main';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!deal) {
    return (
      <Box p={3}>
        <Alert severity="info">No deal found</Alert>
      </Box>
    );
  }

  return (
    <Box p={4} sx={{
      background: theme => `linear-gradient(to bottom, ${alpha(theme.palette.background.default, 0.9)}, ${alpha(theme.palette.background.default, 1)})`,
      minHeight: '100vh',
    }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: '24px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: theme => `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          }
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              background: theme => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
            }}
          >
            {deal.name}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate(-1)}
            startIcon={<ArrowBack />}
            sx={{
              borderRadius: 8,
              px: 2,
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateX(-4px)'
              }
            }}
          >
            Back to Deals
          </Button>
        </Box>

        <Divider sx={{ my: 3 }} />

        <SectionTitle>Hotel Information</SectionTitle>
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 3,
                boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                height: '100%'
              }}
            >
              <Avatar
                sx={{
                  bgcolor: 'primary.light',
                  color: 'primary.main',
                  width: 56,
                  height: 56
                }}
              >
                <Business fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="overline" color="text.secondary">Hotel Name</Typography>
                <Typography variant="h6" fontWeight="medium">
                  {deal.hotel_data.name}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 3,
                boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                height: '100%'
              }}
            >
              <Avatar
                sx={{
                  bgcolor: 'secondary.light',
                  color: 'secondary.main',
                  width: 56,
                  height: 56
                }}
              >
                <LocationOn fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="overline" color="text.secondary">Location</Typography>
                <Typography variant="h6" fontWeight="medium">
                  {[deal.hotel_data.address, deal.hotel_data.city, deal.hotel_data.state].filter(Boolean).join(', ')}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <SectionTitle>Financial Details</SectionTitle>
        <Grid container spacing={4} mb={4}>
          <Grid item xs={12} md={6}>
            <MetricCard>
              <Typography variant="subtitle1" fontWeight="semibold" gutterBottom>
                Deal Summary
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AttachMoney color="primary" />
                <Typography variant="body1" fontWeight="medium">
                  Asking Price:
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {formatCurrency(deal.asking_price)}
              </Typography>

              {deal.recommended_offer && (
                <Box mt={2} sx={{ borderLeft: '3px solid', borderColor: 'success.main', pl: 2 }}>
                  <Typography variant="body1" fontWeight="medium" gutterBottom color="success.main">
                    Recommended Offer:
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    {formatCurrency(deal.recommended_offer)}
                  </Typography>
                </Box>
              )}

              {deal.negotiation_leverage && (
                <Box mt={2}>
                  <Typography variant="body1" fontWeight="medium" gutterBottom>
                    Negotiation Leverage:
                  </Typography>
                  <Chip
                    label={deal.negotiation_leverage}
                    color={
                      deal.negotiation_leverage === 'Strong' ? 'success' :
                      deal.negotiation_leverage === 'Moderate' ? 'primary' : 'warning'
                    }
                    variant="outlined"
                  />
                </Box>
              )}

              {deal.insights?.valuationEstimate && (
                <Box mt={2}>
                  <Typography variant="body1" fontWeight="medium" gutterBottom>
                    Valuation Range:
                  </Typography>
                  <Typography>
                    {formatCurrency(deal.insights.valuationEstimate.low)} - {formatCurrency(deal.insights.valuationEstimate.high)}
                  </Typography>
                </Box>
              )}
            </MetricCard>
          </Grid>

          {deal.insights?.keyMetrics && (
            <Grid item xs={12} md={6}>
              <MetricCard>
                <Typography variant="subtitle1" fontWeight="semibold" gutterBottom>
                  Key Metrics
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <KeyMetricLabel>Total Revenue</KeyMetricLabel>
                    <KeyMetricValue>{formatCurrency(deal.insights.keyMetrics.totalRevenue)}</KeyMetricValue>
                  </Grid>
                  <Grid item xs={6}>
                    <KeyMetricLabel>Total Expenses</KeyMetricLabel>
                    <KeyMetricValue>{formatCurrency(deal.insights.keyMetrics.totalExpenses)}</KeyMetricValue>
                  </Grid>
                  <Grid item xs={6}>
                    <KeyMetricLabel>Net Operating Income</KeyMetricLabel>
                    <KeyMetricValue>{formatCurrency(deal.insights.keyMetrics.netOperatingIncome)}</KeyMetricValue>
                  </Grid>
                  <Grid item xs={6}>
                    <KeyMetricLabel>Profit Margin</KeyMetricLabel>
                    <KeyMetricValue>{deal.insights.keyMetrics.profitMargin}%</KeyMetricValue>
                  </Grid>

                  {/* Added metrics */}
                  {(deal.cap_rate || deal.insights.keyMetrics.capRate) && (
                    <Grid item xs={6}>
                      <KeyMetricLabel>Cap Rate</KeyMetricLabel>
                      <Box display="flex" alignItems="center">
                        <KeyMetricValue>{deal.cap_rate || deal.insights.keyMetrics.capRate}%</KeyMetricValue>
                        <Percent fontSize="small" color="action" sx={{ ml: 0.5 }} />
                      </Box>
                    </Grid>
                  )}

                  {(deal.price_per_key || deal.insights.keyMetrics.pricePerKey) && (
                    <Grid item xs={6}>
                      <KeyMetricLabel>Price per Key</KeyMetricLabel>
                      <KeyMetricValue>{formatCurrency((deal.price_per_key || deal.insights.keyMetrics.pricePerKey) ?? 0)}</KeyMetricValue>
                    </Grid>
                  )}

                  {(deal.revenue_multiple || deal.insights.keyMetrics.revenueMultiple) && (
                    <Grid item xs={6}>
                      <KeyMetricLabel>Revenue Multiple</KeyMetricLabel>
                      <Box display="flex" alignItems="center">
                        <KeyMetricValue>{deal.revenue_multiple || deal.insights.keyMetrics.revenueMultiple}x</KeyMetricValue>
                        <TrendingUp fontSize="small" color="action" sx={{ ml: 0.5 }} />
                      </Box>
                    </Grid>
                  )}

                  {/* Add hotel AI analysis metrics if available */}
                  {hotelAnalysis && (
                    <>
                      {hotelAnalysis.investmentScore && (
                        <Grid item xs={6}>
                          <KeyMetricLabel>Investment Score</KeyMetricLabel>
                          <Box display="flex" alignItems="center">
                            <KeyMetricValue
                              sx={{
                                color: getInvestmentScoreColor(hotelAnalysis.investmentScore)
                              }}
                            >
                              {hotelAnalysis.investmentScore}/100
                            </KeyMetricValue>
                            <Stars fontSize="small" color="action" sx={{ ml: 0.5 }} />
                          </Box>
                        </Grid>
                      )}

                      {hotelAnalysis.valuationInsights?.estimatedValue && (
                        <Grid item xs={6}>
                          <KeyMetricLabel>AI Value Estimate</KeyMetricLabel>
                          <KeyMetricValue>
                            {formatCurrency(hotelAnalysis.valuationInsights.estimatedValue.low)} -
                            {formatCurrency(hotelAnalysis.valuationInsights.estimatedValue.high)}
                          </KeyMetricValue>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </MetricCard>
            </Grid>
          )}
        </Grid>

        {/* Valuation Methods Section */}
        {(deal.valuation_methods || deal.insights?.valuationMethods) && (
          <Box mt={4}>
            <SectionTitle>Valuation Methods</SectionTitle>
            <Grid container spacing={3}>
              {/* Income Approach */}
              {(deal.valuation_methods?.incomeApproach || deal.insights?.valuationMethods?.incomeApproach) && (
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 4,
                      boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: theme => theme.palette.primary.main,
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                      <Avatar sx={{ bgcolor: 'primary.light', width: 42, height: 42 }}>
                        <Percent fontSize="small" />
                      </Avatar>
                      <Typography variant="h6" fontWeight={600}>Income Approach</Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary" paragraph>
                      Based on property's income generation potential using capitalization rate valuation.
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Box mt={2}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <KeyMetricLabel>Estimated Value</KeyMetricLabel>
                          <KeyMetricValue>
                            {formatCurrency(
                              deal.valuation_methods?.incomeApproach?.value ||
                              deal.insights?.valuationMethods?.incomeApproach?.value || 0
                            )}
                          </KeyMetricValue>
                        </Grid>

                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              p: 1.5,
                              bgcolor: 'background.default',
                              borderRadius: 2,
                              mt: 1
                            }}
                          >
                            <Box>
                              <Typography variant="caption" color="text.secondary">Cap Rate</Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {deal.valuation_methods?.incomeApproach?.capRate ||
                                deal.insights?.valuationMethods?.incomeApproach?.capRate || 0}%
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                bgcolor: 'primary.light',
                              }}
                            >
                              <Percent sx={{ color: 'primary.main' }} />
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>
              )}

              {/* Market Approach */}
              {(deal.valuation_methods?.marketApproach || deal.insights?.valuationMethods?.marketApproach) && (
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 4,
                      boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: theme => theme.palette.info.main,
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                      <Avatar sx={{ bgcolor: 'info.light', width: 42, height: 42 }}>
                        <TrendingUp fontSize="small" />
                      </Avatar>
                      <Typography variant="h6" fontWeight={600}>Market Approach</Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary" paragraph>
                      Based on comparable property sales using revenue multiplier analysis.
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Box mt={2}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <KeyMetricLabel>Estimated Value</KeyMetricLabel>
                          <KeyMetricValue>
                            {formatCurrency(
                              deal.valuation_methods?.marketApproach?.value ||
                              deal.insights?.valuationMethods?.marketApproach?.value || 0
                            )}
                          </KeyMetricValue>
                        </Grid>

                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              p: 1.5,
                              bgcolor: 'background.default',
                              borderRadius: 2,
                              mt: 1
                            }}
                          >
                            <Box>
                              <Typography variant="caption" color="text.secondary">Revenue Multiple</Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {deal.valuation_methods?.marketApproach?.multiple ||
                                deal.insights?.valuationMethods?.marketApproach?.multiple || 0}x
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                bgcolor: 'info.light',
                              }}
                            >
                              <TrendingUp sx={{ color: 'info.main' }} />
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>
              )}

              {/* Replacement Cost */}
              {(deal.valuation_methods?.replacementCost || deal.insights?.valuationMethods?.replacementCost) && (
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 4,
                      boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: theme => theme.palette.success.main,
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                      <Avatar sx={{ bgcolor: 'success.light', width: 42, height: 42 }}>
                        <Business fontSize="small" />
                      </Avatar>
                      <Typography variant="h6" fontWeight={600}>Replacement Cost</Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary" paragraph>
                      Based on current construction costs to rebuild the property to similar specifications.
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Box mt={2}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <KeyMetricLabel>Estimated Value</KeyMetricLabel>
                          <KeyMetricValue>
                            {formatCurrency(
                              deal.valuation_methods?.replacementCost?.value ||
                              deal.insights?.valuationMethods?.replacementCost?.value || 0
                            )}
                          </KeyMetricValue>
                        </Grid>

                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              p: 1.5,
                              bgcolor: 'background.default',
                              borderRadius: 2,
                              mt: 1
                            }}
                          >
                            <Box>
                              <Typography variant="caption" color="text.secondary">Cost Per Room</Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {formatCurrency(
                                  deal.valuation_methods?.replacementCost?.costPerKey ||
                                  deal.insights?.valuationMethods?.replacementCost?.costPerKey || 0
                                )}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                bgcolor: 'success.light',
                              }}
                            >
                              <HotelIcon sx={{ color: 'success.main' }} />
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Deal Analysis Section */}
        {deal.insights?.dealAnalysis && (
          <Box mt={4}>
            <SectionTitle>Deal Analysis</SectionTitle>
            <MetricCard>
              <Typography variant="subtitle1" fontWeight="semibold" gutterBottom>
                Asking Price Analysis
              </Typography>
              <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                {deal.insights.dealAnalysis.askingPriceAnalysis}
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="body1" fontWeight="medium">
                      Recommended Offer:
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    {formatCurrency(deal.insights.dealAnalysis.recommendedOfferPrice)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="body1" fontWeight="medium">
                      Negotiation Leverage:
                    </Typography>
                  </Box>
                  <Chip
                    label={deal.insights.dealAnalysis.negotiationLeverage}
                    color={
                      deal.insights.dealAnalysis.negotiationLeverage === 'Strong' ? 'success' :
                      deal.insights.dealAnalysis.negotiationLeverage === 'Moderate' ? 'primary' : 'warning'
                    }
                    sx={{ fontWeight: 'bold' }}
                  />
                </Grid>
              </Grid>
            </MetricCard>
          </Box>
        )}

        {/* Hotel Analysis Integration */}
        {deal.hotel_analysis && (
          <Box mt={4}>
            <SectionTitle>Hotel Analysis Integration</SectionTitle>
            <MetricCard>
              <Typography variant="subtitle1" fontWeight="semibold" gutterBottom>
                Hotel Property Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                This data comes from the comprehensive hotel analysis performed on this property.
              </Typography>

              {/* Market Analysis */}
              {deal.hotel_analysis.marketAnalysis && (
                <Box mt={3}>
                  <Typography variant="h6" color="primary.dark" gutterBottom>
                    Market Analysis
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                    {deal.hotel_analysis.marketAnalysis.summary}
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          Competitive Position:
                        </Typography>
                        <Chip
                          label={deal.hotel_analysis.marketAnalysis.competitivePosition}
                          color={
                            deal.hotel_analysis.marketAnalysis.competitivePosition === 'Strong' ? 'success' :
                            deal.hotel_analysis.marketAnalysis.competitivePosition === 'Moderate' ? 'primary' : 'warning'
                          }
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  <Box>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      Demand Drivers:
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {deal.hotel_analysis.marketAnalysis.demandDrivers.map((driver: string, index: number) => (
                        <Chip key={index} label={driver} color="info" variant="outlined" />
                      ))}
                    </Box>
                  </Box>

                  <Box mt={3}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      Market Risks:
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {deal.hotel_analysis.marketAnalysis.risks.map((risk: string, index: number) => (
                        <Chip key={index} label={risk} color="error" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Property Assessment */}
              {deal.hotel_analysis.propertyAssessment && (
                <Box mt={4}>
                  <Typography variant="h6" color="primary.dark" gutterBottom>
                    Property Assessment
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        Strengths:
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={1}>
                        {deal.hotel_analysis.propertyAssessment.strengths.map((strength: string, index: number) => (
                          <Box key={index} display="flex" alignItems="center" gap={1}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                            <Typography variant="body2">{strength}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        Weaknesses:
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={1}>
                        {deal.hotel_analysis.propertyAssessment.weaknesses.map((weakness: string, index: number) => (
                          <Box key={index} display="flex" alignItems="center" gap={1}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                            <Typography variant="body2">{weakness}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="text.secondary">
                              Recommendations
                            </Typography>
                            <Box component="ul" sx={{ mt: 0.5, pl: 2 }}>
                              {hotelAnalysis.propertyAssessment.recommendations.map((rec: string, index: number) => (
                                <Typography component="li" variant="body2" key={index}>
                                  {rec}
                                </Typography>
                              ))}
                            </Box>
                          </Grid>
                  </Grid>
                </Box>
              )}

              {/* Valuation Insights */}
              {deal.hotel_analysis.valuationInsights && (
                <Box mt={4}>
                  <Typography variant="h6" color="primary.dark" gutterBottom>
                    Valuation Insights
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          Estimated Value Range:
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {formatCurrency(deal.hotel_analysis.valuationInsights.estimatedValue.low)} - {formatCurrency(deal.hotel_analysis.valuationInsights.estimatedValue.high)}
                        </Typography>
                      </Box>

                      <Box mt={3}>
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          Potential ROI:
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" color="success.main">
                          {deal.hotel_analysis.valuationInsights.potentialROI}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        Value Drivers:
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={1}>
                        {deal.hotel_analysis.valuationInsights.valueDrivers.map((driver: string, index: number) => (
                          <Box key={index} display="flex" alignItems="center" gap={1}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                            <Typography variant="body2">{driver}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Investment Score */}
              {deal.hotel_analysis.investmentScore !== undefined && (
                <Box mt={4} display="flex" justifyContent="center">
                  <Box
                    sx={{
                      width: 180,
                      height: 180,
                      borderRadius: '50%',
                      border: '8px solid',
                      borderColor:
                        deal.hotel_analysis.investmentScore >= 80 ? 'success.main' :
                        deal.hotel_analysis.investmentScore >= 60 ? 'primary.main' :
                        deal.hotel_analysis.investmentScore >= 40 ? 'warning.main' : 'error.main',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      boxShadow: 3
                    }}
                  >
                    <Typography variant="h3" fontWeight="bold">
                      {deal.hotel_analysis.investmentScore}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Investment Score
                    </Typography>
                  </Box>
                </Box>
              )}
            </MetricCard>
          </Box>
        )}

        {deal.financial_metrics && (
          <Box mt={4}>
            <SectionTitle>Detailed Financials</SectionTitle>
            <Paper sx={{
              p: 3,
              borderRadius: '16px',
              boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
              overflow: 'hidden'
            }}>
              {(() => {
                // Get ALL unique years across ALL metrics for consistent columns
                const allYears = Array.from(
                  new Set(deal.financial_metrics.map(m => m.year))
                ).sort();

                // Group by financial category
                const groupedMetrics = groupFinancialsByCategory(deal.financial_metrics);

                return Object.entries(groupedMetrics).map(([group, metrics], groupIndex) => {
                  // Create a map of category -> { year -> { value, source } }
                  const categoryMap: Record<string, Record<number, { value: number, source?: string }>> = {};

                  // Populate the map
                  metrics.forEach(metric => {
                    if (!categoryMap[metric.category]) {
                      categoryMap[metric.category] = {};
                    }
                    categoryMap[metric.category][metric.year] = {
                      value: metric.value,
                      source: metric.source
                    };
                  });

                  // Get group color
                  const groupColor = group === 'Revenue' ? 'primary' :
                                    group === 'Expenses' ? 'error' :
                                    group === 'Income' ? 'success' : 'info';

                  return (
                    <Box key={group} mb={4}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 2,
                          pb: 1,
                          borderBottom: 1,
                          borderColor: `${groupColor}.light`
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: `${groupColor}.main`,
                            mr: 1.5
                          }}
                        />
                        <Typography
                          variant="h6"
                          color={`${groupColor}.main`}
                          fontWeight="600"
                        >
                          {group}
                        </Typography>
                      </Box>

                      <Table sx={{
                        tableLayout: 'fixed',
                        width: '100%',
                        '& .MuiTableCell-root': {
                          borderColor: theme => alpha(theme.palette.divider, 0.1),
                        }
                      }}>
                        <TableHead>
                          <TableRow sx={{
                            bgcolor: theme => alpha(theme.palette[groupColor].light, 0.1)
                          }}>
                            <TableCell sx={{ width: '20%', fontWeight: 'bold' }}>Category</TableCell>
                            <TableCell sx={{ width: '20%', fontWeight: 'bold' }}>Item</TableCell>
                            {allYears.map(year => (
                              <TableCell
                                key={year}
                                align="right"
                                sx={{
                                  width: `${60 / allYears.length}%`,
                                  fontWeight: 'bold',
                                  fontSize: '0.9rem'
                                }}
                              >
                                {year}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(categoryMap).map(([category, yearData], index) => {
                            const categoryName = category;

                            return (
                              <TableRow
                                key={category}
                                sx={{
                                  '&:hover': {
                                    bgcolor: theme => alpha(theme.palette.action.hover, 0.05)
                                  },
                                  '.MuiTableCell-root': {
                                    py: 2
                                  }
                                }}
                              >
                                <TableCell sx={{ fontWeight: 500 }}>
                                  {categoryName}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 400 }}>
                                  {/* Item name could be the same as category in this case */}
                                  {category}
                                </TableCell>
                                {allYears.map(year => (
                                  <TableCell key={year} align="right">
                                    {yearData[year] ? (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                        <Typography
                                          variant="body2"
                                          fontWeight="medium"
                                          sx={{
                                            color: yearData[year].value < 0 ? 'error.main' :
                                                  group === 'Revenue' ? 'primary.main' :
                                                  group === 'Income' ? 'success.main' : 'inherit'
                                          }}
                                        >
                                          {formatCurrency(yearData[year].value)}
                                        </Typography>
                                        {yearData[year].source && (
                                          <Box mt={0.5}>
                                            <SourceChip
                                              label={yearData[year].source}
                                              sourceType={yearData[year].source as any}
                                              size="small"
                                            />
                                          </Box>
                                        )}
                                      </Box>
                                    ) : '—'}
                                  </TableCell>
                                ))}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  );
                });
              })()}
            </Paper>
          </Box>
        )}

        {deal.insights && (
          <Box mt={4}>
            <SectionTitle>Investment Analysis</SectionTitle>
            <MetricCard sx={{ p: 3 }}>
              <Typography variant="body1" paragraph>
                {deal.insights.summary}
              </Typography>
              <Grid container spacing={2} mt={2}>
                <Grid item xs={6} sm={3}>
                  <KeyMetricLabel>Risk Level</KeyMetricLabel>
                  <KeyMetricValue>{deal.insights.investmentRisk}</KeyMetricValue>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <KeyMetricLabel>Profitability Score</KeyMetricLabel>
                  <KeyMetricValue>{deal.insights.profitabilityScore}/100</KeyMetricValue>
                </Grid>
              </Grid>
            </MetricCard>
          </Box>
        )}

        {/* Hotel AI Analysis Section */}
        {deal.hotel_id && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box mb={4}>
              <SectionTitle variant="h6" gutterBottom>
                <AutoAwesome color="primary" sx={{ mr: 1 }} />
                AI Valuation Insights
              </SectionTitle>

              {loadingAnalysis ? (
                <Box display="flex" justifyContent="center" my={4}>
                  <CircularProgress size={40} />
                </Box>
              ) : analysisError ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {analysisError}
                </Alert>
              ) : hotelAnalysis ? (
                <Grid container spacing={3}>
                  {/* Market Analysis */}
                  <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: '8px' }}>
                      <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                        Market Analysis
                      </Typography>
                      <Typography variant="body2" paragraph>
                        {hotelAnalysis.marketAnalysis?.summary}
                      </Typography>

                      {hotelAnalysis.marketAnalysis?.competitivePosition && (
                        <Box mt={2}>
                          <Typography variant="caption" color="text.secondary">
                            Competitive Position
                          </Typography>
                          <Typography variant="body2">
                            {hotelAnalysis.marketAnalysis.competitivePosition}
                          </Typography>
                        </Box>
                      )}

                      {hotelAnalysis.marketAnalysis?.demandDrivers && (
                        <Box mt={2}>
                          <Typography variant="caption" color="text.secondary">
                            Key Demand Drivers
                          </Typography>
                          <Box component="ul" sx={{ mt: 0.5, pl: 2 }}>
                            {hotelAnalysis.marketAnalysis.demandDrivers.map((driver: string, index: number) => (
                              <Typography component="li" variant="body2" key={index}>
                                {driver}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  {/* Valuation Insights */}
                  <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: '8px' }}>
                      <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                        Valuation Insights
                      </Typography>

                      {hotelAnalysis.valuationInsights?.valueDrivers && (
                        <Box mt={2} mb={3}>
                          <Typography variant="caption" color="text.secondary">
                            Value Drivers
                          </Typography>
                          <Box component="ul" sx={{ mt: 0.5, pl: 2 }}>
                            {hotelAnalysis.valuationInsights.valueDrivers.map((driver: string, index: number) => (
                              <Typography component="li" variant="body2" key={index}>
                                {driver}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {hotelAnalysis.valuationInsights?.potentialROI && (
                        <Box mt={2}>
                          <Typography variant="caption" color="text.secondary">
                            Potential ROI
                          </Typography>
                          <Typography variant="body1" fontWeight="600" color="primary.main">
                            {hotelAnalysis.valuationInsights.potentialROI}
                          </Typography>
                        </Box>
                      )}

                      {hotelAnalysis.investmentScore && (
                        <Box mt={2} display="flex" alignItems="center">
                          <Typography variant="caption" color="text.secondary" mr={1}>
                            Investment Score:
                          </Typography>
                          <Box
                            component="span"
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: '4px',
                              bgcolor: getInvestmentScoreColor(hotelAnalysis.investmentScore),
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          >
                            {hotelAnalysis.investmentScore}/100
                          </Box>
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  {/* Property Assessment */}
                  <Grid item xs={12}>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: '8px' }}>
                      <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                        Property Assessment
                      </Typography>

                      <Grid container spacing={3}>
                        {hotelAnalysis.propertyAssessment?.strengths && (
                          <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="text.secondary">
                              Strengths
                            </Typography>
                            <Box component="ul" sx={{ mt: 0.5, pl: 2 }}>
                              {hotelAnalysis.propertyAssessment.strengths.map((strength: string, index: number) => (
                                <Typography component="li" variant="body2" key={index}>
                                  {strength}
                                </Typography>
                              ))}
                            </Box>
                          </Grid>
                        )}

                        {hotelAnalysis.propertyAssessment?.weaknesses && (
                          <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="text.secondary">
                              Weaknesses
                            </Typography>
                            <Box component="ul" sx={{ mt: 0.5, pl: 2 }}>
                              {hotelAnalysis.propertyAssessment.weaknesses.map((weakness: string, index: number) => (
                                <Typography component="li" variant="body2" key={index}>
                                  {weakness}
                                </Typography>
                              ))}
                            </Box>
                          </Grid>
                        )}

                        {hotelAnalysis.propertyAssessment?.recommendations && (
                          <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="text.secondary">
                              Recommendations
                            </Typography>
                            <Box component="ul" sx={{ mt: 0.5, pl: 2 }}>
                              {hotelAnalysis.propertyAssessment.recommendations.map((rec: string, index: number) => (
                                <Typography component="li" variant="body2" key={index}>
                                  {rec}
                                </Typography>
                              ))}
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PsychologyIcon />}
                    onClick={() => window.open(`/hotels/${deal.hotel_id}?tab=analysis`, '_blank')}
                  >
                    Run Hotel AI Analysis
                  </Button>
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    No AI analysis available for this hotel. Click to run an analysis.
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}

        <Box mt={4}>
          <Typography variant="caption" color="text.secondary">
            <CalendarMonth sx={{ mr: 0.5, verticalAlign: 'middle' }} />
            Created: {new Date(deal.created_at).toLocaleDateString()}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default DealDetails;
